import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import PropTypes from "prop-types";
import { Popconfirm } from "antd";
import { Button, ButtonGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import {
    closeCustomModal,
    openCustomModal
} from "components/custom/modals";
import {
    getPlanogramDataList,
    deletePlanogramDataByDataId, getPlanogramLayoutList,
    getPlanogramById
} from "api/menu/master-files/planogram-maintenance/actions";
import ImportPlanogramModal from "./import-planogram-modal";

const EditPlanogramDataModal = React.lazy(() =>
    import("components/menu/master-files/planogram-maintenance/edit-planogram-data-modal"));

class PlanogramData extends React.Component {
    constructor(props) {
        super(props);

        this.getColumns = this.getColumns.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleImport = this.handleImport.bind(this);
        this.handleDelete = this.handleDelete.bind(this);

        this.props.getPlanogramDataList(this.props.planogramId);
        this.props.getPlanogramLayoutList(this.props.planogramId);
        this.props.getPlanogramById(this.props.planogramId);
    }

    /*
    + cost: 0
    + itemCode: "JFB01159B"
    + itemDescription: "Bracelet"
    planogramId: 64
    planogramDataId: 64
    + quantity: 6
    + retail: 14
    + slotCode: "745599"
    sortOrder: 2
    + upc: "057952757827"
    */
    getColumns = () => [
        {
            Header: "Slot Code",
            accessor: "slotCode",
        },
        {
            Header: "Item Code",
            accessor: "itemCode",
        },
        {
            Header: "UPC",
            accessor: "upc",
        },
        {
            Header: "Description",
            accessor: "itemDescription",
        },
        {
            Header: "Cost",
            accessor: "cost",
            Cell: ({ original }) => original.cost
        },
        {
            Header: "Retail",
            accessor: "retail",
            Cell: ({ original }) => original.retail
        },
        {
            Header: "Quantity",
            accessor: "quantity",
        },
        {
            Header: "Actions",
            id: "actions",
            Cell: ({ original }) => (
                <div className="text-center">
                    <ButtonGroup>
                        <Button size="sm" onClick={() => this.handleEdit(original.planogramId, original.planogramDataId)}>
                            <FontAwesomeIcon icon="pen" title="Update" />
                        </Button>
                        <Popconfirm
                            title={`Delete "${original.itemCode}"?`}
                            onConfirm={() => this.handleDelete(
                                original.planogramId,
                                original.planogramDataId
                            )}
                            placement="bottomRight"
                            okText="Yes"
                            cancelText="Cancel"
                        >
                            <Button variant="danger" size="sm">
                                <FontAwesomeIcon icon="times" />
                            </Button>
                        </Popconfirm>
                    </ButtonGroup>
                </div>
            ),
            filterable: false,
            width: 100
        }
    ];

    handleEdit = (planogramId, planogramDataId = false) => {
        const modalObj = {
            modalSettings: {
                size: "md",
            },
            header: `${planogramDataId ? "Edit" : "New"} Planogram Data List`,
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <EditPlanogramDataModal
                        planogramId={planogramId}
                        planogramDataId={planogramDataId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSave={() => this.props.closeCustomModal(modalObj)}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    handleDelete = (planogramId, planogramDataId) => {
        this.props.deletePlanogramDataByDataId(planogramId, planogramDataId);
    };

    handleImport = () => {
        const modalObj = {
            modalSettings: {
                size: "sm",
            },
            header: "Import Planogram",
            children: (
                <ImportPlanogramModal
                    planogramId={this.props.planogramId}
                    onSave={() => this.props.closeCustomModal(modalObj)}
                    onCancel={() => this.props.closeCustomModal(modalObj)}
                />
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    render() {
        const {
            loading,
            planogramId,
            planogramDataList,
            planogramLayoutList
        } = this.props;

        const enableImportButton =
            Array.isArray(planogramLayoutList) && planogramLayoutList.length === 0
            && Array.isArray(planogramDataList) && planogramDataList.length === 0;

        return (
            <div className="dashboard">

                <div className="text-right mt-2 mb-2">
                    <Button
                        variant="info"
                        className="mr-2"
                        disabled={!enableImportButton}
                        onClick={() => this.handleImport()}
                    >
                        Import Planogram
                    </Button>
                    <button
                        className="btn btn-success"
                        onClick={() => this.handleEdit(planogramId)}
                    >
                        Add Data
                    </button>
                </div>

                <ReactTable
                    data={planogramDataList}
                    columns={this.getColumns()}
                    headerClassName="table-header"
                    showPagination={true}
                    defaultPageSize={20}
                    pageSizeOptions={[10, 20, 50, 100]}
                    resizable
                    className="-striped -highlight"
                    loading={loading}
                    filterable={true}
                    defaultSorted={[{
                        id: "sortOrder",
                        desc: false
                    }]}
                    defaultFilterMethod={filterCaseInsensitive}
                    style={{
                        height: "calc(100vh - 170px)",
                    }}
                />
            </div>
        );
    }
}

PlanogramData.propTypes = {
    planogramId: PropTypes.number.isRequired
};

const mapStateToProps = state => ({
    loading: state.planogramMaintenance.loading,
    planogramDataList: state.planogramMaintenance.planogramDataList,
    planogramLayoutList: state.planogramMaintenance.planogramLayoutList,
});

const mapStateToDispatch = dispatch => ({
    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch),

    getPlanogramDataList: bindActionCreators(getPlanogramDataList, dispatch),
    getPlanogramLayoutList: bindActionCreators(getPlanogramLayoutList, dispatch),

    deletePlanogramDataByDataId: bindActionCreators(deletePlanogramDataByDataId, dispatch),
    getPlanogramById: bindActionCreators(getPlanogramById, dispatch),
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(PlanogramData);
